<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Categories"
    :items="allCategories || []"
    :headers="headers"
    item-key="Category_SK"
    selection-state-name="selectedCategorySks"
    :loading="allCategoriesLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Category"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Category_Name" label="Category Name" autofocus></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                :items="allCategories || []"
                item-title="Category_Name"
                item-value="Category_SK"
                v-model="editedItem.Parent_Category_FK"
                label="Parent Category"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Categories'"
    item-key="Category_SK"
    display-field="Category_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Category"
    :item-name="itemToDelete?.Category_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_CATEGORIES from '@/graphql/Category/allCategories.gql'
import ADD_CATEGORY from '@/graphql/Category/addCategory.gql'
import UPDATE_CATEGORY from '@/graphql/Category/updateCategory.gql'
import REMOVE_CATEGORY from '@/graphql/Category/removeCategory.gql'
import MERGE_CATEGORY from '@/graphql/Category/mergeCategory.gql'

const store = useStore()

const {
  result: allCategoriesResult,
  loading: allCategoriesLoading,
} = useQuery(ALL_CATEGORIES)
const allCategories = computed(() => allCategoriesResult.value?.allCategories)

const headers = [
  { title: '#', key: 'Category_SK', filterType: 'number', align: 'center' },
  { title: 'Name', key: 'Category_Name', filterType: 'text', align: 'left' },
  { title: 'Parent', key: 'ParentCategory.Category_Name', filterType: 'combo', align: 'left' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Category' : 'Edit Category')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allCategories.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addCategory } = useMutation(ADD_CATEGORY,
  () => ({
    update: (cache, { data: { addCategory } }) => {
      const { allCategories } = cache.readQuery({ query: ALL_CATEGORIES })
      const newData = { allCategories: [...allCategories, addCategory] }
      cache.writeQuery({ query: ALL_CATEGORIES, data: newData })
    }
  })
)

const { mutate: updateCategory } = useMutation(UPDATE_CATEGORY)

const save = () => {
  const input = {
    Category_SK: editedItem.value.Category_SK,
    Category_Name: editedItem.value.Category_Name,
    Parent_Category_FK: editedItem.value.Parent_Category_FK
  }
  if (editedIndex.value > -1) {
    updateCategory({ UpdateCategoryInput: input })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    const { Category_SK: _Category_SK, ...addInput } = input
    addCategory({ AddCategoryInput: addInput })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeCategory } = useMutation(REMOVE_CATEGORY)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeCategory({
    Category_SK: item.Category_SK
  }, {
    update: (cache, { data: { removeCategory } }) => {
      const { allCategories } = cache.readQuery({ query: ALL_CATEGORIES })
      const newData = { allCategories: allCategories.filter((c) => c.Category_SK !== removeCategory) }
      cache.writeQuery({ query: ALL_CATEGORIES, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Category_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeCategory } = useMutation(MERGE_CATEGORY)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeCategory({
    MergeCategoryInput: {
      survivor_Category_SK: mergeData.survivor,
      duplicate_Category_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allCategories } = cache.readQuery({ query: ALL_CATEGORIES })
      const newData = {
        allCategories: allCategories.filter(category => !mergeData.duplicates.includes(category.Category_SK))
      }
      cache.writeQuery({ query: ALL_CATEGORIES, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} categories`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
